import { useState } from "react";
import { Pencil, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/msme-api";

/* Correct a single bill's source data when Tally's Outstanding Balance
 * Report shows a stale voucher/due date for a creditor that isn't
 * tracked bill-wise — without needing to re-upload the whole Excel. */
export default function EditBillModal({ sessionId, row, onClose, onSaved }) {
  const [form, setForm] = useState({
    voucher_no: row.voucher_no || "",
    voucher_date: row.voucher_date || "",
    bill_amount: row.bill_amount ?? "",
    due_date: row.source_due_date || "",
    analysis_type: row.analysis_type || "",
  });
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/sessions/${sessionId}/yearend/${row.id}`, {
        voucher_no: form.voucher_no,
        voucher_date: form.voucher_date,
        bill_amount: form.bill_amount === "" ? null : Number(form.bill_amount),
        due_date: form.due_date,
        analysis_type: form.analysis_type,
      });
      toast.success("Bill corrected — recomputing…");
      await onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update bill");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="edit-bill-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil size={16} /> Correct Bill — {row.ledger_name}
          </DialogTitle>
          <DialogDescription>
            Use this when the source Ageing report shows a stale voucher/due date for a
            creditor that isn&apos;t tracked bill-wise in Tally. Saving clears cached
            results — rerun Computation afterwards to see the corrected outcome.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4 py-2">
          <div className="space-y-1.5">
            <Label>Invoice / Voucher No</Label>
            <Input value={form.voucher_no} onChange={set("voucher_no")} data-testid="edit-bill-voucher-no" />
          </div>
          <div className="space-y-1.5">
            <Label>Bill Amount</Label>
            <Input type="number" value={form.bill_amount} onChange={set("bill_amount")} data-testid="edit-bill-amount" />
          </div>
          <div className="space-y-1.5">
            <Label>Voucher / Bill Date</Label>
            <Input type="date" value={form.voucher_date} onChange={set("voucher_date")} data-testid="edit-bill-voucher-date" />
          </div>
          <div className="space-y-1.5">
            <Label>Source Due Date (optional)</Label>
            <Input type="date" value={form.due_date} onChange={set("due_date")} data-testid="edit-bill-due-date" />
          </div>
          <div className="space-y-1.5 col-span-2">
            <Label>Analysis Type</Label>
            <Input value={form.analysis_type} onChange={set("analysis_type")} data-testid="edit-bill-analysis-type" />
          </div>
        </div>

        <DialogFooter>
          <button
            type="button"
            onClick={onClose}
            className="btn-outline-swiss"
            data-testid="edit-bill-cancel-btn"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="btn-primary-swiss flex items-center gap-2"
            data-testid="edit-bill-save-btn"
          >
            {saving ? <Loader2 className="animate-spin" size={14} /> : <Pencil size={14} />}
            Save &amp; Recompute
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
